// src/components/saved-cars/saved-cars.ts

import Sortable from "sortablejs";
import { toastSuccess } from "../toast/toast";
import { getProxiedImageUrl } from "../../utils/image-proxy";
import { captureGaragePhoto } from "./capture";
import { buildAmazonSearchUrl, AFFILIATE_LINKS_ENABLED } from "../../utils/amazon-affiliate";

const STORAGE_KEY = "garage-wish-saved-cars";
const MAX_SAVED_CARS = 30;
const MAX_IMAGE_WIDTH = 800;
const WEBP_QUALITY = 0.8;

// Inline shopping-bag icon for the hover-reveal "shop the model" button
const CART_ICON_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path><path d="M3 6h18"></path><path d="M16 10a4 4 0 0 1-8 0"></path></svg>`;

export interface SavedCar {
  id: string;
  imageUrl: string;
  imageData?: string; // WebP data URL for local storage
  thumbnailUrl?: string;
  query: string;
  savedAt: number;
}

/**
 * Convert an image URL to a resized WebP data URL
 * Fetches via proxy to avoid CORS, resizes to max width, exports as WebP
 */
export async function imageUrlToDataUrl(imageUrl: string, query = ""): Promise<string> {
  const proxyUrl = getProxiedImageUrl(imageUrl) + "&skipCache=true" + (query ? `&q=${encodeURIComponent(query)}` : "");

  const response = await fetch(proxyUrl);
  if (!response.ok) {
    let source = "";
    let status = response.status;
    try {
      const body = await response.json();
      source = body?.source || "";
      status = body?.status || status;
      console.error("Proxy fetch failed:", body);
    } catch {
      console.error("Proxy fetch failed with status:", response.status);
    }
    if (status === 403 && source) {
      throw new Error(`${source} blocks saving`);
    }
    throw new Error(`Couldn't save image (${status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    return await new Promise<string>((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        let width = img.naturalWidth;
        let height = img.naturalHeight;

        if (width > MAX_IMAGE_WIDTH) {
          height = Math.round((height * MAX_IMAGE_WIDTH) / width);
          width = MAX_IMAGE_WIDTH;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        resolve(canvas.toDataURL("image/webp", WEBP_QUALITY));
      };

      img.onerror = () => reject(new Error("Couldn't decode image"));
      img.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Check if garage is full
 */
export function isGarageFull(): boolean {
  return getSavedCars().length >= MAX_SAVED_CARS;
}

/**
 * Get the maximum number of saved cars allowed
 */
export function getMaxSavedCars(): number {
  return MAX_SAVED_CARS;
}

/**
 * Get all saved cars from localStorage
 */
export function getSavedCars(): SavedCar[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error("Error reading saved cars:", error);
    return [];
  }
}

/**
 * Save a car to localStorage
 */
export function saveCar(
  imageUrl: string,
  imageData: string,
  searchParams: {
    query: string;
  },
): SavedCar {
  const savedCars = getSavedCars();

  // Check garage limit
  if (savedCars.length >= MAX_SAVED_CARS) {
    throw new Error(`Garage full! Remove a car to save more. (Max: ${MAX_SAVED_CARS})`);
  }

  // Check if car is already saved (by image URL)
  if (isCarSaved(imageUrl)) {
    throw new Error("This car is already saved");
  }

  const newCar: SavedCar = {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    imageUrl,
    imageData,
    query: searchParams.query,
    savedAt: Date.now(),
  };

  savedCars.push(newCar);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedCars));
    return newCar;
  } catch (error) {
    console.error("Error saving car:", error);
    throw new Error("Failed to save car. Storage may be full.");
  }
}

/**
 * Remove a car from localStorage by ID
 */
export function removeCar(id: string): boolean {
  const savedCars = getSavedCars();
  const filteredCars = savedCars.filter((car) => car.id !== id);

  if (filteredCars.length === savedCars.length) {
    return false; // Car not found
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredCars));
    return true;
  } catch (error) {
    console.error("Error removing car:", error);
    return false;
  }
}

/**
 * Check if a car is already saved by image URL
 */
export function isCarSaved(imageUrl: string): boolean {
  const savedCars = getSavedCars();
  return savedCars.some((car) => car.imageUrl === imageUrl);
}

/**
 * Clear all saved cars
 */
export function clearAllCars(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing saved cars:", error);
  }
}

/**
 * Reorder saved cars based on new index order
 */
function saveCarOrder(orderedIds: string[]): void {
  const cars = getSavedCars();
  const carMap = new Map(cars.map((car) => [car.id, car]));
  const reordered = orderedIds.map((id) => carMap.get(id)).filter((car): car is SavedCar => car !== undefined);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(reordered));
  } catch (error) {
    console.error("Error saving car order:", error);
  }
}

/**
 * Close all open popovers within a container
 */
function closeAllPopovers(container: HTMLElement): void {
  container.querySelectorAll(".car-popover.open").forEach((p) => p.classList.remove("open"));
}

/**
 * Position popover on mobile (fixed positioning with smart overflow handling)
 */
function positionPopoverMobile(popover: HTMLElement, menuButton: Element): void {
  const isMobile = window.innerWidth <= 768;
  if (!isMobile) {
    popover.style.top = "";
    popover.style.left = "";
    return;
  }

  const buttonRect = menuButton.getBoundingClientRect();
  const popoverWidth = popover.offsetWidth || 224; // min-width: 14rem = 224px
  const viewportWidth = window.innerWidth;
  const padding = 8;

  // Vertical: below the button
  popover.style.top = `${buttonRect.bottom + padding}px`;

  // Horizontal: align right edge with button, but prevent overflow
  let left = buttonRect.right - popoverWidth;

  // Prevent left overflow
  if (left < padding) {
    left = padding;
  }

  // Prevent right overflow
  if (left + popoverWidth > viewportWidth - padding) {
    left = viewportWidth - popoverWidth - padding;
  }

  popover.style.left = `${left}px`;
}

/**
 * Set up popover toggle and click-outside behavior
 */
function setupPopoverBehavior(container: HTMLElement): void {
  // Click on menu button -> toggle popover
  container.addEventListener("click", (e) => {
    const menuButton = (e.target as HTMLElement).closest(".menu-button");
    if (menuButton) {
      const targetId = menuButton.getAttribute("data-popover-target");
      if (targetId) {
        const popover = document.getElementById(targetId);
        const isCurrentlyOpen = popover?.classList.contains("open");
        closeAllPopovers(container);
        if (!isCurrentlyOpen && popover) {
          positionPopoverMobile(popover, menuButton);
          popover.classList.add("open");
        }
      }
    }
  });

  // Click outside -> close popover
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest(".car-popover") && !target.closest(".menu-button")) {
      closeAllPopovers(container);
    }
  });
}

/**
 * Initialize and display saved cars in the garage section
 */
export function initializeSavedCars(): void {
  const garageContainer = document.getElementById("garage-cars-list");
  if (!garageContainer) return;

  displaySavedCars();
  setupPopoverBehavior(garageContainer);

  // Enable drag-and-drop reordering
  Sortable.create(garageContainer, {
    animation: 150,
    delay: 300,
    delayOnTouchOnly: true,
    filter: ".menu-button-wrapper, .menu-button, .car-popover, .garage-shop-button",
    preventOnFilter: false, // Allow click events to pass through to filtered elements
    ghostClass: "sortable-ghost",
    chosenClass: "sortable-chosen",
    dragClass: "sortable-drag",
    onEnd: () => {
      const orderedIds = Array.from(garageContainer.querySelectorAll(".menu-button"))
        .map((btn) => btn.getAttribute("data-car-id"))
        .filter((id): id is string => id !== null);
      saveCarOrder(orderedIds);
    },
  });

  // Take photo button
  const takePhotoBtn = document.getElementById("take-photo-btn");
  takePhotoBtn?.addEventListener("click", captureGaragePhoto);

  // Set up event delegation for delete buttons in popovers
  garageContainer.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const deleteButton = target.closest(".delete-button") as HTMLButtonElement;

    if (deleteButton) {
      const carId = deleteButton.dataset.carId;

      // Check if already in confirmation state
      if (deleteButton.classList.contains("confirm-state")) {
        // Second click - actually delete
        if (carId) {
          const success = removeCar(carId);
          if (success) {
            toastSuccess("Car removed from garage");
            // Close any open popovers
            const openPopover = document.querySelector(".car-popover.open");
            if (openPopover) {
              openPopover.classList.remove("open");
            }
            displaySavedCars(); // Refresh the display
          }
        }
      } else {
        // First click - enter confirmation state
        deleteButton.classList.add("confirm-state");
        deleteButton.textContent = "Confirm removal";
      }
    }
  });

  // Reset delete button when popover opens
  garageContainer.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const menuButton = target.closest(".menu-button") as HTMLButtonElement;

    if (menuButton) {
      const popovertarget = menuButton.getAttribute("data-popover-target");
      if (popovertarget) {
        const popover = document.getElementById(popovertarget);
        if (popover) {
          const deleteButton = popover.querySelector(".delete-button");
          if (deleteButton) {
            deleteButton.classList.remove("confirm-state");
            deleteButton.textContent = "Remove from garage";
          }
        }
      }
    }
  });
}

/**
 * Update the garage tab to show the count of saved cars
 */
export function updateGarageTabCount(): void {
  const garageTab = document.getElementById("tabs-saved-cars");
  if (!garageTab) return;

  const count = getSavedCars().length;
  const span = garageTab.querySelector("span");
  if (span) {
    span.textContent = `My garage (${count}/30)`;
  }
}

/**
 * Display saved cars in the garage
 */
export function displaySavedCars(): void {
  const garageContainer = document.getElementById("garage-cars-list");
  if (!garageContainer) return;

  const savedCars = getSavedCars();

  // Update the tab count
  updateGarageTabCount();

  if (savedCars.length === 0) {
    garageContainer.innerHTML =
      '<li class="empty-message">No cars saved yet. Search for cars and click the save button to add them here!</li>';
    return;
  }

  garageContainer.innerHTML = "";

  savedCars.forEach((car) => {
    const li = document.createElement("li");

    const shopButton = AFFILIATE_LINKS_ENABLED
      ? `<a
        class="garage-shop-button"
        href="${buildAmazonSearchUrl(car.query)}"
        target="_blank"
        rel="sponsored noopener"
        aria-label="Shop ${car.query} model car on Amazon"
      >${CART_ICON_SVG}<span>Shop model</span></a>`
      : "";
    const shopPopoverLink = AFFILIATE_LINKS_ENABLED
      ? `<a
          class="affiliate-link"
          href="${buildAmazonSearchUrl(car.query)}"
          target="_blank"
          rel="sponsored noopener"
        >Shop the model on Amazon</a>`
      : "";

    li.innerHTML = `
      <img src="${car.imageData}" alt="${car.query}" loading="lazy" />
      ${shopButton}
      <div class="menu-button-wrapper">
        <button class="menu-button" data-car-id="${car.id}" data-popover-target="popover-${car.id}">
          <svg><use href="/sprite.svg#icon-menu"></use></svg>
        </button>
      </div>
      <div id="popover-${car.id}" class="car-popover">
        <h3><strong>Search:</strong> ${car.query}</h3>
        ${shopPopoverLink}
        <button class="delete-button" data-car-id="${car.id}">Remove from garage</button>
      </div>
    `;
    garageContainer.appendChild(li);
  });
}
